// State and IndexedDB Manager
import { EditorState } from './editor_state.js';
import { FolderStateManager } from './folder_state_manager.js';
import { PhotoService } from './photo_service.js';
import { ViewportPhotoLoader } from './viewport_photo_loader.js';

export class StateManager {
  constructor() {
    this.db = null;
    this.editorState = new EditorState();
    
    // Photo data service and lazy loading
    this.photoService = new PhotoService(1000); // Max 1000 photos cached
    this.viewportPhotoLoader = new ViewportPhotoLoader(this.photoService);
    
    this.folderStateManager = new FolderStateManager(this.photoService);
    
    // Setup auto-save on state changes (debounced)
    this.saveDebounceTimer = null;
    this.editorState.addEventListener('stateChanged', () => {
      this.debouncedSave();
    });
    
    // Setup localStorage persistence for active photo (per folder)
    this.editorState.addEventListener('photoChanged', (e) => {
      const currentFolderId = this.editorState.get('currentFolderId');
      if (currentFolderId) {
        this.saveActivePhotoToLocalStorage(currentFolderId, e.detail.photoId);
      }
    });
    
    // Setup localStorage persistence for current folder
    this.editorState.addEventListener('currentFolderId', (e) => {
      this.saveCurrentFolderToLocalStorage(e.detail.value);
    });
  }

  /**
   * Initialize IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('EditorDB', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains('fileHandles')) {
          db.createObjectStore('fileHandles', { keyPath: 'guid' });
        }
        
        if (!db.objectStoreNames.contains('editorState')) {
          db.createObjectStore('editorState', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Save file handle to IndexedDB
   */
  async saveFileHandle(guid, fileHandle) {
    const transaction = this.db.transaction(['fileHandles'], 'readwrite');
    const store = transaction.objectStore('fileHandles');
    await store.put({ guid, fileHandle });
  }

  /**
   * Get file handle from IndexedDB
   */
  async getFileHandle(guid) {
    const transaction = this.db.transaction(['fileHandles'], 'readonly');
    const store = transaction.objectStore('fileHandles');
    const request = store.get(guid);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result?.fileHandle);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Load state from IndexedDB
   */
  async loadState() {
    const transaction = this.db.transaction(['editorState'], 'readonly');
    const store = transaction.objectStore('editorState');
    const request = store.get('appState');
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const savedState = request.result?.value;
        if (savedState) {
          this.editorState.loadSerializedState(savedState);
        }
        
        // Load current folder from localStorage
        this.loadCurrentFolderFromLocalStorage();
        
        // Load active photo for the current folder from localStorage
        const currentFolderId = this.editorState.get('currentFolderId');
        if (currentFolderId) {
          const photoId = this.loadActivePhotoFromLocalStorage(currentFolderId);
          if (photoId !== null) {
            this.editorState.set({ activePhotoId: photoId });
          }
        }
        
        resolve(this.editorState.get());
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save active photo ID to localStorage (per folder)
   */
  saveActivePhotoToLocalStorage(folderId, photoId) {
    try {
      // Get existing mapping
      const mappingJson = localStorage.getItem('tinygrid_folderActivePhotos');
      const mapping = mappingJson ? JSON.parse(mappingJson) : {};
      
      // Update mapping for this folder
      if (photoId === null || photoId === undefined) {
        delete mapping[folderId];
      } else {
        mapping[folderId] = photoId;
      }
      
      // Save updated mapping
      localStorage.setItem('tinygrid_folderActivePhotos', JSON.stringify(mapping));
    } catch (error) {
      console.error('Failed to save active photo to localStorage:', error);
    }
  }

  /**
   * Load active photo ID from localStorage for a specific folder
   */
  loadActivePhotoFromLocalStorage(folderId) {
    try {
      if (!folderId) return null;
      
      const mappingJson = localStorage.getItem('tinygrid_folderActivePhotos');
      if (!mappingJson) return null;
      
      const mapping = JSON.parse(mappingJson);
      const photoId = mapping[folderId];
      
      if (photoId !== undefined) {
        const parsedId = parseInt(photoId, 10);
        if (!isNaN(parsedId)) {
          return parsedId;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to load active photo from localStorage:', error);
      return null;
    }
  }

  /**
   * Save current folder ID to localStorage
   */
  saveCurrentFolderToLocalStorage(folderId) {
    try {
      if (folderId === null || folderId === undefined) {
        localStorage.removeItem('tinygrid_currentFolder');
      } else {
        localStorage.setItem('tinygrid_currentFolder', String(folderId));
      }
    } catch (error) {
      console.error('Failed to save current folder to localStorage:', error);
    }
  }

  /**
   * Load current folder ID from localStorage
   */
  loadCurrentFolderFromLocalStorage() {
    try {
      const savedFolderId = localStorage.getItem('tinygrid_currentFolder');
      if (savedFolderId) {
        const folderId = parseInt(savedFolderId, 10);
        if (!isNaN(folderId)) {
          // Only update if different from current state
          const currentFolderId = this.editorState.get('currentFolderId');
          if (currentFolderId !== folderId) {
            this.editorState.set({ currentFolderId: folderId });
          }
        }
      }
    } catch (error) {
      console.error('Failed to load current folder from localStorage:', error);
    }
  }

  /**
   * Save state to IndexedDB (debounced)
   */
  debouncedSave() {
    clearTimeout(this.saveDebounceTimer);
    this.saveDebounceTimer = setTimeout(() => {
      this.saveState();
    }, 500);
  }

  /**
   * Save state to IndexedDB immediately
   */
  async saveState() {
    const stateToSave = this.editorState.getSerializableState();

    const transaction = this.db.transaction(['editorState'], 'readwrite');
    const store = transaction.objectStore('editorState');
    await store.put({ key: 'appState', value: stateToSave });
  }

  /**
   * Get the EditorState instance
   */
  getEditorState() {
    return this.editorState;
  }
  
  /**
   * Get the FolderStateManager instance
   */
  getFolderStateManager() {
    return this.folderStateManager;
  }

  /**
   * Parse URL to determine page and photo
   */
  parseUrl() {
    const path = window.location.pathname;
    
    // Match /editor/edit/{id}
    const editWithIdMatch = path.match(/^\/editor\/edit\/(\d+)$/);
    if (editWithIdMatch) {
      return { page: 'edit', photoId: parseInt(editWithIdMatch[1]) };
    }
    
    // Match /editor/edit
    if (path === '/editor/edit') {
      return { page: 'edit' };
    }
    
    // Match /editor/export
    if (path === '/editor/export') {
      return { page: 'export' };
    }
    
    // Match /editor or /editor/ - always start at photos page
    // (Don't restore last page state to avoid confusing navigation)
    if (path === '/editor' || path === '/editor/') {
      return { page: 'photos' };
    }
    
    // Any other path defaults to photos
    return { page: 'photos' };
  }

  /**
   * Update URL based on page and photo
   */
  updateUrl(page, photo = null, updateHistory = true) {
    let url = '/editor';
    if (page === 'edit' && photo) {
      url = `/editor/edit/${photo.id}`;
    } else if (page === 'edit') {
      url = '/editor/edit';
    } else if (page === 'export') {
      url = '/editor/export';
    }

    if (updateHistory) {
      history.pushState({ page, photoId: photo?.id }, '', url);
    }
  }
}

